/* eslint-disable react-hooks/exhaustive-deps */
import { CloseOutlined, DeleteOutlined } from '@ant-design/icons'
import { Checkbox, Modal, Popconfirm } from 'antd'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import ItemsWithLoading from '@/components/Atoms/ItemsWithLoading/ItemsWithLoading'
import SummaryPanel from '@/components/Atoms/SummaryPanel/SummaryPanel'
import ValidationErrorModal from '@/components/Cart/ValidationErrorModal/ValidationErrorModal'
import OrganisationBar from '@/components/OrganisationBar/OrganisationBar'
import OrganisationsSearch from '@/components/OrganisationsSearch/OrganisationsSearch'
import { SCENARIOS } from '@/constants'
import { ScreenSizeContext } from '@/providers/ScreenSizeProvider'
import { cartService } from '@/services/cart/CartService'
import { cartValidationService } from '@/services/cart/CartValidationService'
import { orderPreviewService } from '@/services/cart/OrderPreviewService'
import { useCartStore } from '@/stores/cart/cartStore'
import { useOrderStore } from '@/stores/cart/orderStore'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type {
  CartProductInterface,
  FeedProductInterface,
  ValidationErrorResponse,
} from '@/types'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { getOfferValidationError } from '@/utils/api/errors/offerValidationError'
import { isClientUser } from '@/utils/auth/userRoles'
import getProductKey from '@/utils/products/getProductKey'
import { toStockOfferLines } from '@/utils/products/offerLines'

import { getAvailabilityStatusText, getTanstackColumns } from './Cart.config'
import cn from './Cart.module.scss'
import { VirtualizedMobileCart } from './VirtualizedComponents/VirtualizedCartList/VirtualizedCartList'
import VirtualizedCartTable from './VirtualizedComponents/VirtualizedCartTable/VirtualizedCartTable'

function Cart() {
  const [products, setProducts] = useState<CartProductInterface[]>([])
  const [productsChecked, setProductsChecked] = useState<
    CartProductInterface[]
  >([])
  const [loading, setLoading] = useState(true)
  const [, setCartTotalPrice] = useState(0)
  const [fetchingError, setFetchingError] = useState<string>('')
  const [showWarning, setShowWarning] = useState(false)
  const [blink, setBlink] = useState(true)
  const [highlightedProductId, setHighlightedProductId] = useState<
    string | null
  >(null)

  const [isCreatingOrder, setIsCreatingOrder] = useState(false)

  const isRestoredRef = useRef(false)

  const [validationError, setValidationError] =
    useState<ValidationErrorResponse | null>(null)

  const prevProductsRef = useRef<CartProductInterface[]>([])
  const initialFetchPerformed = useRef(false)

  const navigate = useNavigate()

  const {
    duplicateProductsQuantities,
    setDuplicateProductsQuantities,
    setProductsQuantity,
    selectedProductKeys,
    setSelectedProductKeys,
    clearSelectedProducts,
    selectedAuthorIds,
    setSelectedAuthorIds,
  } = useCartStore((state) => state)
  const {
    selectedOrganisation,
    organisation,
    scenario,
    currentClient,
    currentUser,
  } = useUserInfoStore((state) => state)
  const isClient = useUserInfoStore((state) => isClientUser(state.tokenRoles))
  const setOrderProducts = useOrderStore((state) => state.setProducts)
  const setPreviewData = useOrderStore((state) => state.setPreviewData)

  const [modal, contextHolder] = Modal.useModal()
  const screen = useContext(ScreenSizeContext)
  const isMobileView = [screen?.lg, screen?.md, screen?.sm, screen?.xs].some(
    Boolean
  )

  const isClientSelectionPending =
    scenario === SCENARIOS.CLIENT_SELECTION_PENDING

  const selectedRowKeys = productsChecked.map((p) => String(getProductKey(p)))

  useEffect(() => {
    if (!isRestoredRef.current) return

    const keys = productsChecked.map((p) => String(getProductKey(p)))
    setSelectedProductKeys(keys)
  }, [productsChecked])

  useEffect(() => {
    if (products.length === 0) return
    if (isRestoredRef.current) return

    const restoredProducts = selectedProductKeys
      .map((key) => products.find((p) => String(getProductKey(p)) === key))
      .filter((p): p is CartProductInterface => p !== undefined)

    if (restoredProducts.length > 0) {
      setProductsChecked(restoredProducts)
    }

    isRestoredRef.current = true
  }, [products])

  useEffect(() => {
    if (!isRestoredRef.current) return

    setProductsChecked((prev) =>
      prev.map((product) => {
        const key = String(getProductKey(product))
        const newProduct = { ...product }

        if ('suppliersStock' in newProduct) {
          newProduct.suppliersStock = newProduct.suppliersStock.map((ss) => ({
            ...ss,
            stock: ss.stock.map((s) => ({ ...s })),
          }))

          const stockItem = newProduct.suppliersStock[0].stock[0]

          if (duplicateProductsQuantities[key] !== undefined) {
            const newQty = duplicateProductsQuantities[key]
            stockItem.quantityInCart = newQty

            if (stockItem.cartInfo) {
              stockItem.cartInfo = { ...stockItem.cartInfo, quantity: newQty }
            } else if (newQty > 0) {
              stockItem.cartInfo = { id: '', quantity: newQty }
            }
          }
        }
        return newProduct
      })
    )

    setProducts((prevProducts) =>
      prevProducts.map((product) => {
        const key = String(getProductKey(product))
        if (duplicateProductsQuantities[key] !== undefined) {
          const newProduct = { ...product }
          newProduct.suppliersStock = newProduct.suppliersStock.map((ss) => ({
            ...ss,
            stock: ss.stock.map((s) => ({ ...s })),
          }))

          const stockItem = newProduct.suppliersStock?.[0]?.stock?.[0]
          if (stockItem) {
            const newQty = duplicateProductsQuantities[key]
            stockItem.quantityInCart = newQty

            if (stockItem.cartInfo) {
              stockItem.cartInfo = { ...stockItem.cartInfo, quantity: newQty }
            } else if (newQty > 0) {
              stockItem.cartInfo = { id: '', quantity: newQty }
            }
          }
          return newProduct
        }
        return product
      })
    )
  }, [duplicateProductsQuantities])

  useEffect(() => {
    if (!cartService.validation.isOrganisationReady()) {
      return
    }

    const fetchCartProducts = async () => {
      setLoading(true)
      setFetchingError('')
      isRestoredRef.current = false

      try {
        const cartData = await cartService.api.fetchCart(setProductsQuantity)

        setCartTotalPrice(cartData.totalPrice)

        const productsWithIndex = cartService.product.addOriginalIndex(
          cartData.products
        )
        setProducts(productsWithIndex)

        productsWithIndex.forEach((product) => {
          const stockItem = product.suppliersStock?.[0]?.stock?.[0]
          const quantity =
            stockItem?.cartInfo?.quantity ?? stockItem?.quantityInCart

          if (stockItem?.storeShortModel && quantity) {
            setDuplicateProductsQuantities(
              String(getProductKey(product)),
              quantity
            )
          }
        })
      } catch (error) {
        console.error('Ошибка при загрузке корзины:', error)
        setFetchingError('Не удалось загрузить корзину.')
        setProducts([])
        setProductsChecked([])
        setCartTotalPrice(0)
        setProductsQuantity(0)
        toast.error('Не удалось загрузить корзину')
      } finally {
        setLoading(false)
      }
    }

    if (initialFetchPerformed.current) {
      if (!loading) {
        fetchCartProducts()
      }
      return
    }

    initialFetchPerformed.current = true
    fetchCartProducts()
  }, [organisation, isClient, selectedOrganisation])

  useEffect(() => {
    const resolvedProductId = cartService.product.detectResolvedProducts(
      prevProductsRef.current,
      products
    )

    if (resolvedProductId) {
      setHighlightedProductId(resolvedProductId)
    }

    prevProductsRef.current = products
  }, [products])

  useEffect(() => {
    if (highlightedProductId) {
      const element = document.getElementById(`row-${highlightedProductId}`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        const timer = setTimeout(() => {
          setHighlightedProductId(null)
        }, 3000)
        return () => clearTimeout(timer)
      }
    }
  }, [highlightedProductId])

  useEffect(() => {
    const shouldShow = cartService.validation.shouldShowWarning()

    if (shouldShow) {
      setShowWarning(true)
      setBlink(true)
      const timer = setTimeout(() => setBlink(false), 3000)
      return () => clearTimeout(timer)
    } else {
      setShowWarning(false)
    }
  }, [isClient, selectedOrganisation])

  const emptyTrashCan = async () => {
    const loadingToast = toast.loading('Очистка корзины...')

    try {
      await cartService.api.deleteCart()

      setProducts([])
      setProductsChecked([])
      setProductsQuantity(0)
      setCartTotalPrice(0)
      clearSelectedProducts()

      const feedStore = useFeedStore.getState()

      feedStore.setProductInfoToCartPresence({})

      const currentFeedProducts = feedStore.products
      if (currentFeedProducts.length > 0) {
        const cleanedFeedProducts = currentFeedProducts.map((feedProd) => {
          const clearItem = <T extends FeedProductInterface>(item: T): T => {
            if (!item.suppliersStock) return item
            return {
              ...item,
              suppliersStock: item.suppliersStock.map((ss) => ({
                ...ss,
                stock: ss.stock.map((s) => ({
                  ...s,
                  cartInfo: null,
                  quantityInCart: 0,
                })),
              })),
            }
          }

          const cleanedParent = clearItem(feedProd)
          if (cleanedParent.children) {
            cleanedParent.children = cleanedParent.children.map(clearItem)
          }
          return cleanedParent
        })

        feedStore.setProducts(cleanedFeedProducts)
      }

      toast.success('Корзина очищена', { id: loadingToast })
    } catch (error) {
      toast.dismiss(loadingToast)
      handleApiError(error, modal)
    }
  }

  const updateProducts = useCallback(
    (newProductsData: CartProductInterface[]) => {
      const productsWithIndex =
        cartService.product.addOriginalIndex(newProductsData)
      setProducts(productsWithIndex)

      const newQuantities =
        cartService.product.extractQuantities(productsWithIndex)
      useCartStore.setState({ duplicateProductsQuantities: newQuantities })

      const presenceMap: Record<string, number> = {}
      Object.entries(newQuantities).forEach(([key, qty]) => {
        if (qty > 0) presenceMap[key] = qty
      })

      const feedStore = useFeedStore.getState()

      feedStore.setProductInfoToCartPresence(presenceMap)

      const currentFeedProducts = feedStore.products
      if (currentFeedProducts.length > 0) {
        const updatedFeedProducts = currentFeedProducts.map((feedProd) => {
          const cleanItem = <T extends FeedProductInterface>(item: T): T => {
            if (!item.suppliersStock) return item

            return {
              ...item,
              suppliersStock: item.suppliersStock.map((ss) => ({
                ...ss,
                stock: ss.stock.map((s) => {
                  const routeKey = s.route ? s.route.join(',') : ''
                  const storeCode = s.storeShortModel?.code
                  const key =
                    storeCode !== undefined
                      ? `${item.nomenclatureDto.code} ${ss.supplierId} ${storeCode} ${routeKey}`.trim()
                      : `${item.nomenclatureDto.code} ${ss.supplierId} ${routeKey}`.trim()

                  if (!presenceMap[key]) {
                    return {
                      ...s,
                      cartInfo: null,
                      quantityInCart: 0,
                    }
                  }

                  return {
                    ...s,
                    quantityInCart: presenceMap[key],
                    cartInfo: s.cartInfo
                      ? { ...s.cartInfo, quantity: presenceMap[key] }
                      : { id: '', quantity: presenceMap[key] },
                  }
                }),
              })),
            }
          }

          const updatedParent = cleanItem(feedProd)

          if (updatedParent.children) {
            updatedParent.children = updatedParent.children.map(cleanItem)
          }

          return updatedParent
        })

        feedStore.setProducts(updatedFeedProducts)
      }

      setProductsChecked((prevChecked) => {
        return prevChecked
          .map((checkedProduct) => {
            const key = String(getProductKey(checkedProduct))
            return productsWithIndex.find(
              (p) => String(getProductKey(p)) === key
            )
          })
          .filter((p): p is CartProductInterface => p !== undefined)
      })
    },
    []
  )

  const removeCheckedProductsFromCart = async () => {
    if (!productsChecked.length) return

    const count = productsChecked.length
    const loadingToast = toast.loading('Удаление товаров...', {
      description: `${count} товар(ов)`,
    })

    try {
      const updatedCart = await cartService.api.deleteProducts(productsChecked)

      updateProducts(updatedCart.products)
      setProductsChecked([])
      setProductsQuantity(updatedCart.products.length)
      setCartTotalPrice(updatedCart.totalPrice)

      toast.success('Товары удалены', {
        id: loadingToast,
        description: `Удалено: ${count} товар(ов)`,
      })
    } catch (error) {
      toast.dismiss(loadingToast)
      handleApiError(error, modal)
    }
  }

  const deleteById = useCallback(
    async (itemId: string) => {
      const product = products.find((p) => {
        const stockItem = p.suppliersStock?.[0]?.stock?.[0]
        return stockItem?.cartInfo?.id === itemId
      })

      const loadingToast = toast.loading('Удаление товара...')

      try {
        const updatedCart = await cartService.api.deleteProductById({ itemId })

        updateProducts(updatedCart.products)
        setProductsQuantity(updatedCart.products.length)
        setCartTotalPrice(updatedCart.totalPrice)

        toast.success('Товар удален', {
          id: loadingToast,
          description: product?.nomenclatureDto.description,
        })
      } catch (error) {
        toast.dismiss(loadingToast)
        handleApiError(error, modal)
      }
    },
    [products, updateProducts, setProductsQuantity, modal]
  )

  const getSummaryTotals = () => {
    return cartService.calculation.getSummaryTotals(
      productsChecked,
      duplicateProductsQuantities
    )
  }

  const handleCreateOrder = async () => {
    if (cartValidationService.hasUnorderableProducts(productsChecked)) {
      toast.error('В заказ можно отправить только товары в наличии', {
        description:
          'Снимите отметку с товаров без остатка или удалите их из корзины',
      })
      return
    }

    const availableProducts =
      cartValidationService.filterAvailableProducts(productsChecked)

    const logicallyChecked = availableProducts.filter((product) => {
      const key = String(getProductKey(product))
      const stockItem = product.suppliersStock?.[0]?.stock?.[0]

      const quantityInCart =
        duplicateProductsQuantities[key] ??
        stockItem?.cartInfo?.quantity ??
        stockItem?.quantityInCart ??
        0

      return quantityInCart > 0
    })

    const orderableProducts =
      cartValidationService.filterOrderableProducts(logicallyChecked)

    if (orderableProducts.length === 0) {
      toast.warning('Нет доступных товаров для заказа', {
        description: 'Выберите товары с наличием',
      })
      return
    }

    const validationResult = cartValidationService.canCreateOrder()
    if (!validationResult.isValid) {
      toast.error(validationResult.errorMessage!)
      return
    }

    setOrderProducts(orderableProducts)

    const loadingToast = toast.loading('Подготовка заказа...', {
      description: `${orderableProducts.length} товар(ов)`,
    })

    try {
      setIsCreatingOrder(true)
      const preview = await orderPreviewService.getOrderPreview(
        currentClient?.id ?? null,
        orderableProducts
      )

      setPreviewData(preview)
      toast.dismiss(loadingToast)
      navigate('/order')
    } catch (error: unknown) {
      toast.dismiss(loadingToast)

      const offerError = getOfferValidationError(error)
      if (offerError) {
        setValidationError(offerError)
      } else {
        handleApiError(error, modal)
      }
    } finally {
      setIsCreatingOrder(false)
    }
  }

  const getSortedProducts = () => {
    return cartService.sorting.sortProducts(products)
  }

  const getUniqueAuthors = (): { id: string; name: string }[] => {
    const map = new Map<string, string>()
    products.forEach((p) => {
      const author = p.authorShortDto
      if (author?.id) {
        map.set(author.id, author.description || author.name || 'Неизвестный')
      }
    })
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }))
  }

  const uniqueAuthors = useMemo(() => getUniqueAuthors(), [products])

  const columns = useMemo(
    () =>
      getTanstackColumns(
        deleteById,
        duplicateProductsQuantities,
        selectedOrganisation,
        organisation,
        updateProducts,
        uniqueAuthors
      ),
    [
      deleteById,
      duplicateProductsQuantities,
      selectedOrganisation,
      organisation,
      updateProducts,
      uniqueAuthors,
    ]
  )

  const getFilteredProducts = () => {
    const sorted = getSortedProducts()
    if (selectedAuthorIds.length === 0) return sorted
    return sorted.filter(
      (p) =>
        p.authorShortDto?.id && selectedAuthorIds.includes(p.authorShortDto.id)
    )
  }

  const getAvailableProducts = () => {
    return cartService.validation.filterAvailableProducts(products)
  }

  const areAllAvailableProductsChecked = () => {
    const availableProducts = getAvailableProducts()
    const checkedKeys = new Set(selectedRowKeys)
    return (
      availableProducts.length > 0 &&
      availableProducts.every((product) =>
        checkedKeys.has(String(getProductKey(product)))
      )
    )
  }

  const isIndeterminate = () =>
    productsChecked.length > 0 && !areAllAvailableProductsChecked()

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setProductsChecked(getAvailableProducts())
    } else {
      setProductsChecked([])
    }
  }

  const totals = getSummaryTotals()
  const unorderableTotals = cartService.calculation.getUnorderableTotals(
    productsChecked,
    duplicateProductsQuantities
  )
  const isOrgSelected = Object.keys(selectedOrganisation).length > 0

  const refetchCart = useCallback(async () => {
    setLoading(true)
    setFetchingError('')

    try {
      const cartData = await cartService.api.fetchCart(setProductsQuantity)
      setCartTotalPrice(cartData.totalPrice)

      const productsWithIndex = cartService.product.addOriginalIndex(
        cartData.products
      )
      setProducts(productsWithIndex)

      productsWithIndex.forEach((product) => {
        const stockItem = product.suppliersStock?.[0]?.stock?.[0]
        const quantity =
          stockItem?.cartInfo?.quantity ?? stockItem?.quantityInCart

        if (stockItem?.storeShortModel && quantity) {
          setDuplicateProductsQuantities(
            String(getProductKey(product)),
            quantity
          )
        }
      })

      setProductsChecked((prevChecked) => {
        return prevChecked
          .map((checkedProduct) => {
            const key = String(getProductKey(checkedProduct))
            return productsWithIndex.find(
              (p) => String(getProductKey(p)) === key
            )
          })
          .filter((p): p is CartProductInterface => p !== undefined)
      })
    } catch (error) {
      setFetchingError('Не удалось обновить корзину.')
      toast.error('Не удалось обновить корзину')
    } finally {
      setLoading(false)
    }
  }, [setProductsQuantity, setDuplicateProductsQuantities])

  const handleModalClose = useCallback(() => {
    setValidationError(null)
    refetchCart()
  }, [refetchCart])

  const totalSelectedItems = totals.selectedCount

  return (
    <section className={cn.cart}>
      {contextHolder}

      <ValidationErrorModal
        error={validationError}
        lines={toStockOfferLines(products)}
        onClose={handleModalClose}
      />

      {/* Header */}
      {isClient ? (
        <div className={cn.titleWithoutOrganisationsSearch}>Корзина</div>
      ) : (
        <div className={cn.titleWithOrganisationsSearch}>
          <span className={cn.title}>Корзина</span>
          <section
            className={
              isOrgSelected
                ? [cn.searchBar, cn.organisationSelected].join(' ')
                : cn.searchBar
            }
          >
            <OrganisationsSearch />
          </section>
        </div>
      )}

      <OrganisationBar />

      <ItemsWithLoading
        items={products}
        loading={loading}
        loaderType="modal"
        empty={
          <div
            className={
              isOrgSelected
                ? [cn.emptyCart, cn.organisationSelected].join(' ')
                : cn.emptyCart
            }
          >
            <p>Корзина пуста</p>
            <Link to="/">
              <button>Вернуться на главную</button>
            </Link>
          </div>
        }
        error={<></>}
        loadingError={fetchingError}
        loaderPaddingTop={64}
      >
        <div className={cn.content}>
          {showWarning && (
            <div className={`${cn.warningBanner} ${blink ? cn.blink : ''}`}>
              ⚠️ Клиент не выбран. Заказ будет сделан поставщику.
            </div>
          )}
          {currentUser?.organisationShortModel?.isLegal === false && (
            <div className={cn.warningBanner}>
              ⚠️ Организация не активирована
            </div>
          )}

          {isMobileView ? (
            <>
              <div className={cn.mobileSelectAllContainer}>
                <Checkbox
                  checked={areAllAvailableProductsChecked()}
                  indeterminate={isIndeterminate()}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                >
                  Выбрать всё
                </Checkbox>
              </div>
              <VirtualizedMobileCart
                products={products}
                deleteById={deleteById}
                highlightedProductId={highlightedProductId}
                updateProducts={updateProducts}
                productsChecked={productsChecked}
                onCheck={(checked, prod) => {
                  if (checked) {
                    setProductsChecked((prev) => {
                      if (
                        prev.some(
                          (p) => getProductKey(p) === getProductKey(prod)
                        )
                      ) {
                        return prev
                      }
                      return [...prev, prod]
                    })
                  } else {
                    setProductsChecked((prev) =>
                      prev.filter(
                        (p) => getProductKey(p) !== getProductKey(prod)
                      )
                    )
                  }
                }}
                allProducts={products}
                mode="cart"
              />
            </>
          ) : (
            <div
              className={
                isOrgSelected
                  ? [cn.table, cn.organisationSelected].join(' ')
                  : cn.table
              }
            >
              <div className={cn.authorChips}>
                {selectedAuthorIds.map((id) => {
                  const author = getUniqueAuthors().find((a) => a.id === id)
                  if (!author) return null
                  return (
                    <div key={id} className={cn.authorChip}>
                      <span className={cn.authorChipAvatar}>
                        {author.name.charAt(0).toUpperCase()}
                      </span>
                      <span>{author.name}</span>
                      <button
                        className={cn.authorChipRemove}
                        onClick={() =>
                          setSelectedAuthorIds(
                            selectedAuthorIds.filter((x) => x !== id)
                          )
                        }
                      >
                        ×
                      </button>
                    </div>
                  )
                })}
              </div>
              <VirtualizedCartTable<CartProductInterface>
                data={getFilteredProducts()}
                columns={columns}
                rowKey={(record) => String(getProductKey(record))}
                rowSelection={{
                  selectedRowKeys,
                  onChange: (_keys, rows) => setProductsChecked([...rows]),
                  canSelectAll: (record) =>
                    cartService.validation.isProductAvailable(record),
                }}
                onRow={(record) => ({
                  id: `row-${String(getProductKey(record))}`,
                  onClick: () => {},
                })}
                highlightedProductId={highlightedProductId}
                rowClassName={(record) =>
                  cartService.sorting.getRowClassName(
                    record,
                    highlightedProductId,
                    cn.noStockRow,
                    cn.highlightRow
                  )
                }
                getRowTooltip={(record) => getAvailabilityStatusText(record)}
              />
            </div>
          )}

          {/* Summary Totals */}
          {productsChecked.length > 0 && (
            <SummaryPanel
              totalItems={totalSelectedItems}
              totals={[
                {
                  label: 'Итого, BYN',
                  value: totals.totalOpt,
                  hint: unorderableTotals
                    ? `в т. ч. недоступные для заказа: ${unorderableTotals.totalOpt}`
                    : undefined,
                },
                {
                  label: 'Итого РРЦ, BYN',
                  value: totals.totalRetail,
                  hint: unorderableTotals
                    ? `в т. ч. недоступные для заказа: ${unorderableTotals.totalRetail}`
                    : undefined,
                },
              ]}
              blueTheme={isOrgSelected}
              className={cn.summaryOverride}
            />
          )}

          {/* Action Buttons */}
          <div className={cn.buttons}>
            <div className={cn.leftPart}>
              {/* Clear Cart */}
              <Popconfirm
                title="Очистка корзины"
                description="Хотите очистить корзину?"
                onConfirm={emptyTrashCan}
                onCancel={() => null}
                icon={null}
                okText="Да"
                cancelText="Нет"
                overlayClassName={
                  isOrgSelected
                    ? [cn.popconfirm, cn.organisationSelected].join(' ')
                    : cn.popconfirm
                }
              >
                <button
                  className={isOrgSelected ? cn.organisationSelected : ''}
                >
                  <DeleteOutlined />
                  <span>Очистить корзину</span>
                </button>
              </Popconfirm>

              {/* Delete Checked */}
              {!productsChecked.length ? (
                <button
                  className={isOrgSelected ? cn.organisationSelected : ''}
                  onClick={() =>
                    toast.warning('Ни один товар не отмечен', {
                      description: 'Выберите товары для удаления',
                    })
                  }
                >
                  <CloseOutlined />
                  <span>Удалить отмеченное</span>
                </button>
              ) : (
                <Popconfirm
                  title="Удаление товаров"
                  description="Хотите удалить товары из корзины?"
                  onConfirm={removeCheckedProductsFromCart}
                  onCancel={() => null}
                  icon={null}
                  okText="Да"
                  cancelText="Нет"
                  overlayClassName={
                    isOrgSelected
                      ? [cn.popconfirm, cn.organisationSelected].join(' ')
                      : cn.popconfirm
                  }
                >
                  <button
                    className={isOrgSelected ? cn.organisationSelected : ''}
                  >
                    <CloseOutlined />
                    <span>Удалить отмеченное</span>
                  </button>
                </Popconfirm>
              )}
            </div>

            {/* Create Order */}
            <div className={cn.rightPart}>
              <button
                className={isOrgSelected ? cn.organisationSelected : ''}
                onClick={handleCreateOrder}
                disabled={isClientSelectionPending || isCreatingOrder}
              >
                {isCreatingOrder ? 'Создание...' : 'Создать заказ'}
              </button>
            </div>
          </div>
        </div>
      </ItemsWithLoading>
    </section>
  )
}

export default Cart
